import React, { useRef, useState, useCallback } from 'react'
import SignaturePad, { type SignaturePadHandle } from './SignaturePad'
import { SignaturePadLock, useSignatureIdentity } from './SignatureAuthGate'
import type { SignatureAuth } from '../utils/signatureAuth'

export interface FieldBlockProps {
  label: string
  description?: string
  required?: boolean
  options?: string[]
  value?: any
  onChange?: (value: any) => void
  disabled?: boolean
  children?: React.ReactNode
  suggestions?: Array<{ label: string; data?: any }>
  onSelectSuggestion?: (label: string, data?: any) => void
  loading?: boolean
  error?: string
}

const TextInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, children, suggestions, onSelectSuggestion, loading, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <div className="join w-full">
      <input className="input input-bordered join-item w-full" placeholder="Type here" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
      {children}
    </div>
    {loading && (
      <div className="mt-1 space-y-2 border rounded-lg p-3">
        <div className="skeleton h-4 w-3/4"></div>
        <div className="skeleton h-4 w-2/3"></div>
        <div className="skeleton h-4 w-1/2"></div>
      </div>
    )}
    {Array.isArray(suggestions) && suggestions.length > 0 && (
      <ul className="menu bg-base-100 rounded-box border mt-1 w-full">
        {suggestions.map((s, idx) => (
          <li key={`${s.label}-${idx}`}><a onClick={() => onSelectSuggestion?.(s.label, s.data)}>{s.label}</a></li>
        ))}
      </ul>
    )}
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const TextareaBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, children, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <div className="join w-full">
      <textarea className="textarea textarea-bordered join-item w-full" rows={3} placeholder="Enter text" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
      {children}
    </div>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const NumberInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <input type="number" className="input input-bordered w-full" placeholder="0" value={value ?? ''} onChange={e => onChange?.(e.target.value === '' ? undefined : Number(e.target.value))} disabled={disabled} />
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const DateInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <input type="date" className="input input-bordered w-full" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const EmailInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, children, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <div className="join w-full">
      <input type="email" className="input input-bordered join-item w-full" placeholder="name@example.com" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
      {children}
    </div>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const TelInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, children, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <div className="join w-full">
      <input type="tel" className="input input-bordered join-item w-full" placeholder="(555) 000-0000" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
      {children}
    </div>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const PasswordInputBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <input type="password" className="input input-bordered w-full" placeholder="••••••••" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const SelectBlock: React.FC<FieldBlockProps> = ({ label, description, required, options, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <select className="select select-bordered w-full" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled}>
      <option value="">Select an option</option>
      {(options || ['Option 1', 'Option 2']).map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const CheckboxBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label cursor-pointer justify-start gap-3">
      <input type="checkbox" className="checkbox checkbox-primary" checked={!!value} onChange={e => onChange?.(e.target.checked)} disabled={disabled} />
      <span className="label-text font-medium">
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </span>
    </label>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const RadioBlock: React.FC<FieldBlockProps> = ({ label, description, required, options, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <div className="flex flex-col gap-2">
      {(options || ['Option A', 'Option B']).map(opt => (
        <label key={opt} className="label cursor-pointer justify-start gap-3">
          <input type="radio" className="radio radio-primary" checked={value === opt} onChange={() => onChange?.(opt)} disabled={disabled} />
          <span className="label-text">{opt}</span>
        </label>
      ))}
    </div>
    {description && <div className="text-xs text-base-content/60 mt-1">{description}</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

export interface SignatureValue {
  dataUrl: string
  clientDate: string
  auth?: SignatureAuth
}

const SignatureDateBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => (
  <div className="form-control w-full max-w-xl">
    <label className="label"><span className="label-text font-medium">{label}{required && <span className="text-error ml-1">*</span>}</span></label>
    <input type="date" className="input input-bordered w-full" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled} />
    {description
      ? <div className="text-xs text-base-content/60 mt-1">{description}</div>
      : <div className="text-xs text-base-content/60 mt-1">Auto-filled from the linked signature.</div>}
    {error && <div className="text-error text-xs mt-1">{error}</div>}
  </div>
)

const SignatureBlock: React.FC<FieldBlockProps> = ({ label, description, required, value, onChange, disabled, error }) => {
  const signaturePadRef = useRef<SignaturePadHandle>(null)
  const [signatureDate] = useState(() => new Date().toISOString().slice(0, 10))
  const currentValue = value as SignatureValue | undefined | null
  const signedImage = typeof currentValue?.dataUrl === 'string' ? currentValue.dataUrl : ''
  const displayDate = currentValue?.clientDate || signatureDate
  const requireIdentity = !!onChange && !disabled
  const { unlocked, requestUnlock, lock, authRef, confirmPasswordModal } = useSignatureIdentity(currentValue)

  const handleEnd = useCallback(() => {
    if (disabled) return
    const sig = signaturePadRef.current
    if (!sig || sig.isEmpty()) return
    const auth = authRef.current
    if (requireIdentity && !auth?.acknowledged) return
    const dataUrl = sig.toDataURL('image/png')
    onChange?.({ dataUrl, clientDate: signatureDate, ...(auth ? { auth } : {}) })
  }, [authRef, disabled, onChange, requireIdentity, signatureDate])

  const handleClear = useCallback(() => {
    if (disabled) return
    signaturePadRef.current?.clear()
    if (requireIdentity) lock()
    onChange?.(undefined)
  }, [disabled, lock, onChange, requireIdentity])

  const pad = disabled && signedImage ? (
    <div className="border border-neutral-300 rounded-lg bg-white overflow-hidden opacity-80">
      <div className="px-2 py-1 text-xs text-neutral-500 border-b border-neutral-200 bg-white">Signed</div>
      <img src={signedImage} alt="" className="w-full h-[120px] object-contain bg-white pointer-events-none" />
    </div>
  ) : (
    <SignaturePad
      ref={signaturePadRef}
      onEnd={handleEnd}
      disabled={disabled}
    />
  )

  return (
    <div className="form-control w-full max-w-xl">
      <label className="label">
        <span className="label-text font-medium">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </span>
      </label>
      <div className="border border-base-300 rounded-lg p-4 bg-base-200/50">
        {description && (
          <p className="text-sm text-base-content/90 mb-3">{description}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            {requireIdentity ? (
              <SignaturePadLock unlocked={unlocked} onUnlock={() => { void requestUnlock() }}>
                {pad}
              </SignaturePadLock>
            ) : pad}
            {disabled && !signedImage && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-base-100/85 p-3">
                <span className="text-sm text-base-content/70 text-center">Not your turn to sign</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <span className="text-xs text-base-content/60">Date</span>
            <input
              type="text"
              className="input input-bordered input-sm w-32"
              value={new Date(displayDate).toLocaleDateString()}
              readOnly
              disabled={disabled}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm mt-1"
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      {error && <div className="text-error text-xs mt-1">{error}</div>}
      {requireIdentity && confirmPasswordModal}
    </div>
  )
}

export const FORM_BLOCKS: Record<string, React.FC<FieldBlockProps>> = {
  text: TextInputBlock,
  textarea: TextareaBlock,
  number: NumberInputBlock,
  date: DateInputBlock,
  email: EmailInputBlock,
  tel: TelInputBlock,
  password: PasswordInputBlock,
  select: SelectBlock,
  checkbox: CheckboxBlock,
  radio: RadioBlock,
  signature: SignatureBlock,
  signatureDate: SignatureDateBlock,
}



